import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiList } from 'react-icons/fi';

const RANK_COLORS = ['#6366F1','#3B82F6','#10B981','#F59E0B','#EF4444','#A855F7','#06B6D4','#F97316','#EC4899','#14B8A6'];

const fmt  = (n) => Number(n || 0).toLocaleString('en-US');
const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

// "Total" renders the status mix as stacked segments; the other metrics
// render a single bar in the metric's own color.
const BAR_METRICS = [
  { key: 'total',    label: 'Total',    fmt,        color: null      },
  { key: 'approved', label: 'Approved', fmt,        color: '#10B981' },
  { key: 'pending',  label: 'Pending',  fmt,        color: '#F59E0B' },
  { key: 'overdue',  label: 'Overdue',  fmt,        color: '#F97316' },
  { key: 'value',    label: 'Value $',  fmt: fmtM,  color: '#6366F1' },
];

const SortableTh = ({ label, sortKey, currentSort, onSort }) => {
  const active = currentSort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none tw-whitespace-nowrap"
    >
      {label}{active && <span className="tw-ml-1 tw-text-gray-400">{currentSort.dir === 'desc' ? '▼' : '▲'}</span>}
    </th>
  );
};

SortableTh.propTypes = {
  label: PropTypes.string.isRequired,
  sortKey: PropTypes.string.isRequired,
  currentSort: PropTypes.object.isRequired,
  onSort: PropTypes.func.isRequired,
};

// Per-person waiver breakdown card with Chart / Table views (mirrors the
// contract dashboard's ContractSalesInchargeChart UX). Generic over the
// grouping dimension: labelKey names the field in each row, drillKey the
// detailed-data filter param. Rows drill down to the dashboard's shared
// slide-over via onItemClick(title, extraParams). All rows are rendered —
// the body scrolls when the list is long.
const WaiverBreakdownChart = ({
  title, Icon, iconColor, personLabel, labelKey, drillKey, drillTitlePrefix,
  data, loading, error, onItemClick, fullHeight,
}) => {
  const [metric,    setMetric]    = useState('total');
  const [view,      setView]      = useState('list');
  const [tableSort, setTableSort] = useState({ key: 'total', dir: 'desc' });

  if (loading) return (
    <Card padding="none" className={`tw-border tw-border-gray-200 tw-shadow-sm ${fullHeight ? 'tw-h-full' : ''}`}>
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card padding="none" className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm tw-mb-0">Error loading {title.toLowerCase()} data</p>
    </Card>
  );

  const activeMetric = BAR_METRICS.find(m => m.key === metric) || BAR_METRICS[0];
  const items  = [...(data || [])].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const maxVal = Math.max(...items.map(r => r[metric] || 0), 1);

  const tableRows = [...(data || [])].sort((a, b) => {
    const diff = (b[tableSort.key] || 0) - (a[tableSort.key] || 0);
    return tableSort.dir === 'desc' ? diff : -diff;
  });

  const totals = tableRows.reduce((s, r) => ({
    total:    s.total    + (r.total    || 0),
    approved: s.approved + (r.approved || 0),
    pending:  s.pending  + (r.pending  || 0),
    rejected: s.rejected + (r.rejected || 0),
    overdue:  s.overdue  + (r.overdue  || 0),
    value:    s.value    + (r.value    || 0),
  }), { total: 0, approved: 0, pending: 0, rejected: 0, overdue: 0, value: 0 });

  const handleSort = (key) => {
    setTableSort(prev =>
      prev.key === key
        ? { ...prev, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    );
  };

  const handleClick = (row) => {
    if (!onItemClick) return;
    onItemClick(`${drillTitlePrefix}: ${row[labelKey]}`, { [drillKey]: [row[labelKey]] });
  };

  return (
    // padding="none" — the card manages its own flush header/body/footer
    <Card padding="none" className={`tw-border tw-border-gray-200 tw-shadow-sm ${fullHeight ? 'tw-h-full' : ''}`}>
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon size={15} className={iconColor} />
          <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">{title}</h3>
          <span className="tw-text-[10px] tw-text-gray-400">{items.length} people</span>
        </div>

        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          {/* View toggle */}
          <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
            <button type="button" onClick={() => setView('list')}
              className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${view === 'list' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}>
              Chart
            </button>
            <button type="button" onClick={() => setView('table')}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 tw-border-l tw-border-l-gray-200 ${view === 'table' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}>
              <FiList size={11} /> Table
            </button>
          </div>

          {/* Metric toggle — chart view only */}
          {view === 'list' && (
            <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
              {BAR_METRICS.map((opt, idx) => (
                <button type="button" key={opt.key} onClick={() => setMetric(opt.key)}
                  className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Chart view ── */}
      {view === 'list' && (
        <>
          <div className="tw-p-4 tw-space-y-1.5 tw-max-h-80 tw-overflow-y-auto">
            {items.length === 0 ? (
              <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-8 tw-mb-0">No data</p>
            ) : items.map((row, i) => {
              const pct   = maxVal > 0 ? ((row[metric] || 0) / maxVal) * 100 : 0;
              const color = RANK_COLORS[i % RANK_COLORS.length];
              const seg   = (v) => (row.total > 0 ? (v / row.total) * pct : 0);
              return (
                <div
                  key={row[labelKey]}
                  onClick={() => handleClick(row)}
                  className="tw-group tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-rounded hover:tw-bg-primary-50/60 tw-px-2 tw-py-1.5 tw-transition-colors"
                >
                  <span
                    className="tw-w-5 tw-h-5 tw-rounded-xl tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-[10px] tw-font-bold"
                    style={{ background: color }}>
                    {i + 1}
                  </span>
                  <span className="tw-w-24 tw-flex-shrink-0 tw-text-xs tw-font-semibold tw-text-gray-700 tw-truncate" title={row[labelKey]}>
                    {row[labelKey] || 'Unknown'}
                  </span>

                  <div className="tw-flex-1 tw-h-4 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden tw-flex"
                    title={`Approved ${fmt(row.approved)} · Pending ${fmt(row.pending)} · Rejected ${fmt(row.rejected)} · Overdue ${fmt(row.overdue)}`}>
                    {metric === 'total' ? (
                      <>
                        <div className="tw-h-full" style={{ width: `${seg(row.approved)}%`, background: '#10B981' }} />
                        <div className="tw-h-full" style={{ width: `${seg(row.pending)}%`,  background: '#F59E0B' }} />
                        <div className="tw-h-full" style={{ width: `${seg(row.rejected)}%`, background: '#EF4444' }} />
                      </>
                    ) : (
                      <div className="tw-h-full tw-rounded-xl tw-transition-all tw-duration-500"
                        style={{ width: `${pct}%`, background: activeMetric.color, opacity: 0.85 }} />
                    )}
                  </div>

                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0 tw-w-28 tw-justify-end">
                    {row.overdue > 0 && metric !== 'overdue' && (
                      <span className="tw-text-[10px] tw-leading-none tw-font-bold tw-px-1.5 tw-py-1 tw-rounded-xl tw-bg-orange-100 tw-text-orange-600 tw-whitespace-nowrap">
                        {fmt(row.overdue)} OVD
                      </span>
                    )}
                    <span className="tw-text-xs tw-font-bold tw-tabular-nums group-hover:tw-hidden" style={{ color: activeMetric.color || '#111827' }}>
                      {activeMetric.fmt(row[metric] || 0)}
                    </span>
                    <span className="tw-hidden group-hover:tw-inline tw-text-[10px] tw-text-gray-500 tw-whitespace-nowrap">
                      <span className="tw-text-emerald-600 tw-font-semibold">{row.approved}A</span>
                      {' · '}<span className="tw-text-amber-600 tw-font-semibold">{row.pending}P</span>
                      {' · '}<span className="tw-text-red-500 tw-font-semibold">{row.rejected}R</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="tw-px-5 tw-py-2 tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-3 tw-text-[10px] tw-text-gray-400">
              <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-w-2 tw-h-2 tw-rounded-xl" style={{ background: '#10B981' }} />Approved</span>
              <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-w-2 tw-h-2 tw-rounded-xl" style={{ background: '#F59E0B' }} />Pending</span>
              <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-w-2 tw-h-2 tw-rounded-xl" style={{ background: '#EF4444' }} />Rejected</span>
            </div>
            <span className="tw-text-[10px] tw-text-gray-300 tw-italic">Click any row for details</span>
          </div>
        </>
      )}

      {/* ── Table view ── */}
      {view === 'table' && (
        <div className="tw-overflow-auto tw-max-h-[420px]">
          <table className="tw-w-full tw-text-xs tw-border-collapse">
            <thead>
              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-8">#</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600 tw-min-w-28">{personLabel}</th>
                <SortableTh label="Total"    sortKey="total"    currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Approved" sortKey="approved" currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Pending"  sortKey="pending"  currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Rejected" sortKey="rejected" currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Overdue"  sortKey="overdue"  currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Value"    sortKey="value"    currentSort={tableSort} onSort={handleSort} />
                <th className="tw-px-3 tw-py-2" />
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr
                  key={row[labelKey]}
                  onClick={() => handleClick(row)}
                  className={`tw-border-b tw-border-gray-100 tw-cursor-pointer ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-primary-50/60 tw-transition-colors`}
                >
                  <td className="tw-px-3 tw-py-2">
                    <span className="tw-w-5 tw-h-5 tw-rounded-xl tw-inline-flex tw-items-center tw-justify-center tw-text-white tw-text-[10px] tw-font-bold"
                      style={{ background: RANK_COLORS[i % RANK_COLORS.length] }}>
                      {i + 1}
                    </span>
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap">{row[labelKey] || 'Unknown'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-gray-900">{fmt(row.total)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-emerald-600">{fmt(row.approved)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-amber-600">{fmt(row.pending)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-red-500">{row.rejected ? fmt(row.rejected) : '—'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-orange-600">{row.overdue ? fmt(row.overdue) : '—'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-primary-600">{row.value ? fmtM(row.value) : '—'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-primary-400">↗</td>
                </tr>
              ))}
            </tbody>
            {tableRows.length > 0 && (
              <tfoot>
                <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                  <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs" colSpan={2}>Total ({tableRows.length} people)</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-900">{fmt(totals.total)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600">{fmt(totals.approved)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600">{fmt(totals.pending)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-red-500">{fmt(totals.rejected)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-orange-600">{fmt(totals.overdue)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-primary-600">{fmtM(totals.value)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Card>
  );
};

WaiverBreakdownChart.propTypes = {
  title: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  iconColor: PropTypes.string,
  personLabel: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
  drillKey: PropTypes.string.isRequired,
  drillTitlePrefix: PropTypes.string.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.any,
  onItemClick: PropTypes.func,
  fullHeight: PropTypes.bool,
};
WaiverBreakdownChart.defaultProps = {
  iconColor: 'tw-text-primary-500', data: [], loading: false, error: null,
  onItemClick: null, fullHeight: false,
};

export default WaiverBreakdownChart;
